import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";

import contentfulApi from "../../utils/contentful";
import { callToLearnMoreButtonClicked } from "utils/segment";

import PageHeader from "../../components/PageHeader";
import Button from "../../components/Button";
import Row from "../../components/Row";
import Col from "../../components/Col";
import Container from "../../components/Container";
import HowTheAppWorks from "../../components/HowTheAppWorks";
import GetStarted from "../../components/GetStarted";

import Page404 from "../Responses/Page404";

import TaskCard from "./TaskCard";

const Activity = (props) => {
  const [data, setData] = useState({});
  const [redirect, setRedirect] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    contentfulApi
      .getEntries({
        content_type: "activity",
        "fields.slug": props.match.params.slug,
      })
      .then((response) => {
        if (response.items.length === 0) {
          setRedirect(true);
        } else {
          setData(Object.assign(response.items[0].fields));
          setDataLoaded(true);
        }
      });
  }, [props]);

  if (!dataLoaded && redirect) {
    return <Page404 />;
  }

  if (!dataLoaded) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>
          {data.activityTitle} Services for Seniors - Papa - Family On-demand
        </title>
        <meta
          name="title"
          content={`${data.activityTitle} Services for Seniors - Papa - Family On-demand`}
        ></meta>
        <meta
          name="description"
          content="Papa helps seniors and older adults with grocery shopping. We pick our customers up, take them to the supermarket, carry their bags and bring them back home."
        ></meta>
        {/* <!-- Open Graph / Facebook --> */}
        <meta property="og:type" content="website"></meta>
        <meta
          property="og:url"
          content="https://www.papa.com/papa-pal-qualified-application/"
        ></meta>
        <meta
          property="og:title"
          content={`${data.activityTitle} Services for Seniors - Papa - Family On-demand`}
        ></meta>
        <meta
          property="og:description"
          content="Papa helps seniors and older adults with grocery shopping. We pick our customers up, take them to the supermarket, carry their bags and bring them back home."
        ></meta>
        <meta
          property="og:image"
          content="http://papa.com/wp-content/uploads/2017/10/1296x728_10_Things_to_Add_to_Your_Caregiver_Toolkit.jpg"
        ></meta>
        {/* <!-- Twitter --> */}
        <meta property="twitter:card" content="summary_large_image"></meta>
        <meta
          property="twitter:url"
          content="https://www.papa.com/papa-pal-qualified-application/"
        ></meta>
        <meta
          property="twitter:title"
          content={`${data.activityTitle} Services for Seniors - Papa - Family On-demand`}
        ></meta>
        <meta
          property="twitter:description"
          content="Papa helps seniors and older adults with grocery shopping. We pick our customers up, take them to the supermarket, carry their bags and bring them back home."
        ></meta>
        <meta
          property="twitter:image"
          content="http://papa.com/wp-content/uploads/2017/10/1296x728_10_Things_to_Add_to_Your_Caregiver_Toolkit.jpg"
        ></meta>
      </Helmet>

      <div className="activity">
        <PageHeader>
          <Row>
            <Col md={6}>
              <h1>{data.headerHeading}</h1>
              <hr />
              <p>{data.headerSubheading}</p>

              <div className="hidden-desktop text-center text-uppercase font-600 mb-10">
                {data.headerCtaText}
              </div>
              <Button
                href="tel:+1-800-348-7951"
                onClick={callToLearnMoreButtonClicked}
              >
                <span className="hidden-mobile">{data.headerCtaText}</span>{" "}
                1-800-348-7951
              </Button>
            </Col>
          </Row>

          <img
            data-src={
              data.headerImage &&
              data.headerImage.fields.file.url + "?w=580&q=80"
            }
            alt="Papa"
            className="page-header__image lazyload"
          />
        </PageHeader>

        <div className="services">
          <Container>
            <Row>
              <Col md={{ span: 6, offset: 3 }}>
                <div className="section-headings text-center">
                  <h3>{data.servicesSubheading}</h3>
                  <h2 className="text-center">{data.servicesHeading}</h2>
                  <p className="mb-0">{data.servicesCopy}</p>
                </div>
              </Col>
            </Row>

            <Row>
              {data.services &&
                data.services.map((service, index) => (
                  <Col
                    key={`service_${index}`}
                    md={{ span: 4, offset: index % 2 === 0 ? 1 : 2 }}
                  >
                    <TaskCard
                      heading={service.fields.heading}
                      icon={
                        service.fields.image &&
                        service.fields.image.fields.file.url
                      }
                    >
                      {service.fields.copy}
                    </TaskCard>
                  </Col>
                ))}
            </Row>

            <div className="text-center">
              <Button
                href="tel:+1-800-348-7951"
                onClick={callToLearnMoreButtonClicked}
              >
                <span className="hidden-mobile">{data.headerCtaText}</span>{" "}
                1-800-348-7951
              </Button>
            </div>
          </Container>
        </div>

        <div
          className="image-block"
          style={{
            backgroundImage: `url(${
              data.separatorImage &&
              data.separatorImage.fields.file.url + "?fm=jpg&fl=progressive"
            })`,
          }}
        />

        <div className="how-it-works">
          <Container>
            <Row>
              <Col md={{ span: 6, offset: 3 }}>
                <div className="section-headings text-center">
                  <h3 className="mb-25">{data.stepsSubheading}</h3>
                  <h2 className="mb-30 text-center">{data.stepsHeading}</h2>
                  <p className="mb-0">{data.stepsCopy}</p>
                </div>
              </Col>
            </Row>

            {data.steps &&
              data.steps.map((step, index) => (
                <Row key={`step_${index}`} className="step align-center">
                  <Col md={6}>
                    <img
                      src={
                        step.fields.image && step.fields.image.fields.file.url
                      }
                      alt="Step"
                      className="img-responsive"
                    />
                  </Col>

                  <Col md={6} className="flex align-start">
                    <div className="number flex align-center justify-center mr-15">
                      {index + 1}
                    </div>
                    <div className="content">
                      <h4 className="title mb-15">{step.fields.heading}</h4>
                      <p>{step.fields.longDescription}</p>
                    </div>
                  </Col>
                </Row>
              ))}

            <div className="text-center">
              <Button
                href="tel:+1-800-348-7951"
                onClick={callToLearnMoreButtonClicked}
              >
                <span className="hidden-mobile">{data.headerCtaText}</span>{" "}
                1-800-348-7951
              </Button>
            </div>
          </Container>
        </div>

        {props.match.params.slug === "video-visits" && (
          <HowTheAppWorks page="virtualVisits" />
        )}

        <GetStarted hideCall={true} />
      </div>
    </>
  );
};

export default Activity;
